/**
 * Comanda cu plata la livrare — partea din browser.
 *
 * Markup-ul e in `snippets/cod-form.liquid` (textele stau in Liquid, unde stiu
 * limba paginii); aici e doar purtarea: deschis/inchis, pasii, verificare,
 * trimitere.
 *
 * Trimiterea NU trece prin cos. Se citeste formularul de PRODUS exact cum o
 * facea si butonul Releasit — varianta, cantitatea si proprietatile
 * `properties[...]` (unde formularul de gravura si-a scris deja datele) — si
 * se trimite totul la `/apps/gravura/cod`, proxy-ul semnat de Shopify.
 * Serviciul creeaza comanda reala, cu plata ramasa datorata.
 *
 * Stilistica si fluxul vin din consola (`GET /apps/gravura/aspect`). Fluxul
 * `quiz` leaga cele doua formulare intr-un singur popup, in pasi: gravura,
 * apoi datele de livrare. Clientul nu mai iese si nu mai intra.
 */

/** Aceleasi reguli de telefon ca pe server — clientul afla AICI, nu dupa refuz. */
const TELEFON = {
  MD: { national: /^0\d{8}$/, international: /^\+373\d{8}$/ },
  RO: { national: /^0\d{9}$/, international: /^\+40\d{9}$/ },
};

const telefonBun = (brut, tara) => {
  const curatat = String(brut ?? '').replace(/[\s().\-]/g, '').replace(/^00/, '+');
  const regula = TELEFON[tara];
  return Boolean(regula && (regula.national.test(curatat) || regula.international.test(curatat)));
};

/** Aceeasi regula ca pe server. Gol e VALID: emailul e optional. */
const emailBun = (brut) => {
  const curatat = String(brut ?? '').trim();
  return curatat === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(curatat);
};

/** Umbrele, in aceleasi trepte ca in consola. */
const UMBRE = {
  fara: null,
  moale: '0 6px 20px rgba(0, 0, 0, 0.12)',
  tare: '0 14px 38px rgba(0, 0, 0, 0.28)',
};

/**
 * Hexul, ca triplet „R G B".
 *
 * Tema isi scrie culorile asa (`--text-color: 28 28 28`) tocmai ca fiecare
 * regula sa poata compune cu transparenta: `rgb(var(--text-color) / 0.6)`.
 * Daca schimbam doar `color`-ul containerului, zecile de reguli care compun
 * asa raman pe culoarea VECHE — de aceea contorul, notele si cotele ieseau
 * negru-pe-negru pe un preset intunecat. Rescriind chiar tokenii, tot ce e
 * inauntru se recalculeaza singur, si al nostru, si al temei.
 */
const tripletRgb = (hex) => {
  const curat = String(hex ?? '').replace('#', '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(curat)) return null;
  const plin = curat.length === 3 ? curat.split('').map((c) => c + c).join('') : curat;
  const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(plin.slice(i, i + 2), 16));
  return `${r} ${g} ${b}`;
};

/**
 * Unde se tine minte adresa, si cat.
 *
 * In browserul clientului, nu la noi — la fel ca autofill-ul. Se scrie DOAR
 * dupa o comanda reusita: pe un calculator din familie, datele pe jumatate
 * scrise de altcineva n-au ce cauta in campurile urmatorului. Nota comenzii
 * nu se pastreaza niciodata: e despre comanda aia, nu despre om.
 */
const CHEIA_ADRESEI = 'mlx_cod_adresa';
const PASTRARE_ADRESA_MS = 90 * 24 * 60 * 60 * 1000;
const CAMPURI_ADRESA = ['nume', 'telefon', 'email', 'tara', 'oras', 'adresa', 'judet', 'postal'];

/** Campurile care dispar la ridicarea din magazin: piesa nu pleaca nicaieri. */
const CAMPURI_DOAR_CURIER = ['oras', 'adresa', 'judet', 'postal'];

/**
 * Proba dintr-o adresa: `?aspect_flux=quiz`, `?aspect_mod=popup`.
 *
 * Fluxul e o setare a magazinului, deci ca sa vezi quiz-ul pe o tema de lucru
 * ar trebui sa-l pornesti pentru TOTI clientii. Parametrii astia il pornesc
 * doar in fila ta: nu se salveaza nicaieri, nu ating consola si nu schimba ce
 * vede altcineva. Se accepta doar valori din listele stiute.
 */
const suprascriereDinAdresa = (aspect) => {
  try {
    const parametri = new URLSearchParams(window.location.search);
    const flux = parametri.get('aspect_flux');
    const modCod = parametri.get('aspect_mod');
    const modGravura = parametri.get('aspect_mod_gravura');

    if (flux === 'quiz' || flux === 'separat') aspect.flux = flux;
    if (aspect.flux === 'quiz') { aspect.cod.mod = 'popup'; aspect.gravura.mod = 'popup'; }
    if (['panou', 'popup'].includes(modCod)) aspect.cod.mod = modCod;
    if (['desfasurat', 'pliat', 'popup'].includes(modGravura)) aspect.gravura.mod = modGravura;
  } catch { /* fara parametri, ramane ce spune consola */ }
  return aspect;
};

/** Etichete prea lungi pentru un rand de rezumat, scurtate la ce inseamna. */
const ETICHETE_SCURTE = {
  'Previzualizare înainte de livrare': 'Previzualizare',
  'Previzualizare inainte de livrare': 'Previzualizare',
};

/** Radacina magazinului: pe pietele cu prefix, `/cart.js` singur da 404. */
const radacinaMagazinului = () => window.Shopify?.routes?.root ?? '/';

const citesteJson = (nod) => {
  try { return JSON.parse(nod?.textContent ?? '{}'); } catch { return {}; }
};

class CodForm extends HTMLElement {
  connectedCallback() {
    this.vorbe = citesteJson(this.querySelector('[data-rol="vorbe"]'));
    this.variante = citesteJson(this.querySelector('[data-rol="variante"]'));
    this.panou = this.querySelector('[data-rol="panou"]');
    this.deschide = this.querySelector('[data-rol="deschide"]');
    this.trimite = this.querySelector('[data-rol="trimite"]');
    this.succes = this.querySelector('[data-rol="succes"]');
    this.trimiteEticheta = this.trimite?.textContent ?? '';
    this.inZbor = false;

    this.deschide?.addEventListener('click', () => this.comuta());
    this.trimite?.addEventListener('click', () => this.trimiteComanda());
    this.querySelector('[data-rol="card"]')?.addEventListener('click', (ev) => {
      this.achitaAcum(ev.currentTarget);
    });

    // Cand gravura se valideaza (si abia atunci apare macheta), rezumatul
    // deschis se reface singur: omul isi corecteaza textul fara sa inchida
    // fereastra si vede desenul aparand, nu un loc gol.
    for (const semn of ['gravura:validat', 'gravura:schimbat']) {
      document.addEventListener(semn, () => {
        if (this.dialog?.open || (this.panou && !this.panou.hidden)) {
          this.arataAvertismentulGravurii();
          this.actualizeazaRezumatul();
        }
      });
    }

    // Eroarea unui camp se stinge cand omul se apuca sa-l repare.
    this.panou?.addEventListener('input', (ev) => {
      const camp = ev.target.closest('[data-camp]');
      camp?.querySelector('[data-rol="eroare"]')?.setAttribute('hidden', '');
    });

    // Enter intr-un camp COD ar trimite FORMULARUL DE PRODUS (suntem inauntrul
    // lui) — adica ar pune piesa in cos in loc sa trimita comanda ramburs.
    this.panou?.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Enter' || ev.target.tagName === 'TEXTAREA') return;
      ev.preventDefault();
      this.trimiteComanda();
    });

    // Schimbarea tarii schimba si exemplul de telefon — altfel un moldovean
    // care alege Romania vede in continuare formatul 06x.
    this.querySelector('[data-camp="tara"] select')?.addEventListener('change', (ev) => {
      const telefon = this.querySelector('[data-camp="telefon"] input');
      if (telefon) telefon.placeholder = ev.target.value === 'RO' ? '07xx xxx xxx' : '06x xxx xxx';
      this.potrivesteLivrarea();
    });

    // Livrare sau ridicare. Chips-uri buton, nu radio (fara `name` in
    // formularul de produs); starea sta aici, in `modLivrare` si `ridicare`.
    this.modLivrare = 'curier';
    this.ridicare = { cand: 'maine' };
    this.querySelector('[data-rol="mod-livrare"]')?.addEventListener('click', (ev) => {
      const chip = ev.target.closest('[data-mod]');
      if (chip) this.alegeLivrarea(chip.dataset.mod);
    });
    const momentele = this.querySelector('[data-rol="ridicare"]');
    momentele?.addEventListener('click', (ev) => {
      const chip = ev.target.closest('[data-cand]');
      if (chip) this.alegeMomentul(chip.dataset.cand);
    });
    const ziua = this.querySelector('[data-rol="ridicare-data"]');
    if (ziua) {
      // Doar zile de azi inainte: o ridicare in trecut nu inseamna nimic.
      ziua.min = new Date().toISOString().slice(0, 10);
      ziua.addEventListener('change', () => { this.ridicare.data = ziua.value; });
    }

    // Sugestiile legate in consola: bifabile in formular sau carduri pe
    // pagina. Nu opresc nimic daca nu vin — comanda merge si fara ele.
    this.upsellAlese = new Set();
    if (this.dataset.sursa !== 'cos' && this.dataset.handle) this.incarcaUpsell();

    // Verificarea pe BLUR, nu doar la trimitere: omul afla ca telefonul e scurt
    // cand pleaca din camp, nu dupa ce a completat toata adresa.
    for (const nume of CAMPURI_ADRESA) {
      const control = this.querySelector(`[data-camp="${nume}"] input`);
      control?.addEventListener('blur', () => this.verificaUnul(nume));
    }

    this.incarcaAdresa();
    this.potrivesteLivrarea();
    this.aplicaAspect();
  }

  /**
   * Ridicarea din magazin e doar pentru Moldova.
   *
   * Magazinul e unul singur, in Chisinau. Pe o comanda din Romania, „ridicare
   * din magazin" ar fi o promisiune pe care n-avem cum s-o tinem — asa ca
   * optiunea dispare cu totul, nu doar refuza la trimitere. Cand ramane un
   * singur drum, grupul de chips-uri dispare si el: o alegere cu o singura
   * varianta nu e o alegere, e un buton care deruteaza.
   */
  potrivesteLivrarea() {
    const doarCurier = (this.valoare('tara') || this.dataset.tara) !== 'MD';
    const grup = this.querySelector('[data-rol="mod-livrare"]');
    const chip = grup?.querySelector('[data-mod="magazin"]');
    if (chip) chip.hidden = doarCurier;
    if (grup) grup.hidden = doarCurier;
    if (doarCurier && this.modLivrare === 'magazin') this.alegeLivrarea('curier');
  }

  /** Adresa de data trecuta, daca e proaspata. Orice eroare = formular gol. */
  incarcaAdresa() {
    try {
      const brut = localStorage.getItem(CHEIA_ADRESEI);
      if (!brut) return;
      const salvat = JSON.parse(brut);
      if (!salvat?.cand || Date.now() - salvat.cand > PASTRARE_ADRESA_MS) {
        localStorage.removeItem(CHEIA_ADRESEI);
        return;
      }
      for (const nume of CAMPURI_ADRESA) {
        const control = this.querySelector(`[data-camp="${nume}"] input, [data-camp="${nume}"] select`);
        if (control && salvat[nume]) control.value = salvat[nume];
      }
    } catch { /* fara memorie, formularul e gol — nimic pierdut */ }
  }

  salveazaAdresa() {
    try {
      const date = { cand: Date.now() };
      for (const nume of CAMPURI_ADRESA) date[nume] = this.valoare(nume);
      localStorage.setItem(CHEIA_ADRESEI, JSON.stringify(date));
    } catch { /* mod privat, cota plina — comanda a plecat oricum */ }
  }

  vorba(cheie, inlocuiri = {}) {
    let text = this.vorbe[cheie] ?? '';
    for (const [nume, valoare] of Object.entries(inlocuiri)) {
      text = text.replaceAll(`{${nume}}`, String(valoare));
    }
    return text;
  }

  /**
   * Stilistica si fluxul, reglate din consola. Orice esec lasa stilul temei si
   * purtarea de baza — formularul nu atarna dupa o setare.
   */
  async aplicaAspect() {
    try {
      const raspuns = await fetch('/apps/gravura/aspect');
      if (!raspuns.ok) return;
      const intreg = suprascriereDinAdresa(await raspuns.json());
      const aspect = intreg?.cod;
      if (!aspect) return;

      this.picteaza(aspect);

      const limba = this.dataset.limba === 'en' ? 'en' : 'ro';
      if (aspect.eticheta?.[limba]) this.deschide.textContent = aspect.eticheta[limba];

      if (intreg.flux === 'quiz') await this.pregatesteQuizul();
      else if (aspect.mod === 'popup') this.pregatestePopup();
    } catch { /* stilul temei ramane */ }
  }

  /** Culorile, gradientul, coltul si umbra — puse doar cand chiar s-a ales ceva. */
  picteaza(aspect) {
    const pune = (nume, valoare, clasa) => {
      if (!valoare) return;
      this.style.setProperty(nume, valoare);
      if (clasa) this.classList.add(clasa);
    };

    // Gradientul bate culoarea simpla: e alegerea mai specifica a omului.
    const fundal = aspect.gradientDeLa && aspect.gradientLa
      ? `linear-gradient(${aspect.gradientUnghi ?? 135}deg, ${aspect.gradientDeLa}, ${aspect.gradientLa})`
      : aspect.accent;
    pune('--cod-accent', fundal, 'cod--accent');
    pune('--cod-accent-text', aspect.accentText, 'cod--accent-text');
    // Tokenii temei, rescrisi inauntru — vezi comentariul lui `tripletRgb`.
    const textTriplet = tripletRgb(aspect.text);
    const fundalTriplet = tripletRgb(aspect.fundal);
    if (textTriplet) this.style.setProperty('--text-color', textTriplet);
    if (fundalTriplet) this.style.setProperty('--background', fundalTriplet);

    pune('--cod-fundal', aspect.fundal, 'cod--fundal');
    pune('--cod-text', aspect.text, 'cod--text');
    pune('--cod-umbra', UMBRE[aspect.umbra], 'cod--umbra');

    if (aspect.radius !== null && aspect.radius !== undefined) {
      this.style.setProperty('--cod-radius', `${aspect.radius}px`);
      this.classList.add('cod--radius');
    }
  }

  /** Fereastra, cu ce se pune in ea. Aceeasi pentru popup si pentru quiz. */
  faDialogul(clasa) {
    const dialog = document.createElement('dialog');
    dialog.className = clasa;

    const inchide = document.createElement('button');
    inchide.type = 'button';
    inchide.className = 'cod__dialog-inchide';
    inchide.setAttribute('aria-label', this.dataset.limba === 'en' ? 'Close' : 'Închide');
    inchide.textContent = '×';
    inchide.addEventListener('click', () => dialog.close());
    dialog.append(inchide);

    return dialog;
  }

  /** Modul popup: panoul si confirmarea se muta intr-un <dialog> peste pagina. */
  pregatestePopup() {
    if (this.dialog) return;

    const dialog = this.faDialogul('cod__dialog');
    // Mutarea NU strica nimic: controalele n-au `name`, iar ascultatorii stau
    // pe elemente, nu pe pozitia lor in pagina.
    dialog.append(this.panou, this.succes);
    this.append(dialog);
    this.panou.hidden = false;
    this.dialog = dialog;

    // Confirmarea trebuie sa ramana vizibila si dupa inchiderea ferestrei.
    dialog.addEventListener('close', () => {
      if (this.succes.hidden) return;
      this.append(this.succes);
      this.deschide.hidden = true;
      this.querySelector('.cod__soapta')?.setAttribute('hidden', '');
    });
  }

  /**
   * Fluxul quiz: UN popup, doi pasi.
   *
   * Pasul 1 e chiar corpul customizerului de gravura — MUTAT, nu copiat: asa
   * campurile raman aceleasi noduri, cu ascultatorii, jetonul si verificarile
   * lor. Un formular clonat ar arata la fel si n-ar valida nimic.
   *
   * Daca piesa nu se graveaza (nu exista `gravura-form` pe pagina), quiz-ul
   * are un singur pas si arata exact ca popup-ul obisnuit.
   */
  async pregatesteQuizul() {
    if (this.dialog) return;

    const gravura = await this.asteaptaGravura();
    const corpGravura = gravura?.querySelector('[data-rol="corp"]') ?? null;

    if (!corpGravura) { this.pregatestePopup(); return; }

    this.gravura = gravura;
    const dialog = this.faDialogul('cod__dialog cod__dialog--quiz');

    // Indicatorul de pas: unde sunt si cat a mai ramas.
    // Progresul: o bara care se umple, plus randul de text de sub ea. Bara se
    // citeste dintr-o privire („mai am putin"), textul spune exact unde esti
    // si e singurul lucru pe care il poate rosti un cititor de ecran.
    this.progres = document.createElement('div');
    this.progres.className = 'cod__progres';
    this.progresSegmente = [];

    this.indicator = document.createElement('p');
    this.indicator.className = 'cod__pas-indicator';
    this.indicator.setAttribute('role', 'status');
    this.indicator.setAttribute('aria-live', 'polite');

    const pasGravura = document.createElement('div');
    pasGravura.className = 'cod__pas';
    pasGravura.dataset.pas = '1';
    const titluGravura = document.createElement('p');
    titluGravura.className = 'cod__pas-titlu';
    titluGravura.textContent = this.vorba('pasGravura');
    pasGravura.append(titluGravura, corpGravura);

    // Mesajul pasului 1 STA in pasul 1.
    //
    // A stat in panoul de livrare, adica in pasul 2 — ascuns. Clientul apasa
    // „Continua" cu gravura incompleta, iar raspunsul se scria intr-un nod cu
    // inaltime zero: butonul parea pur si simplu mort. Cel mai scump fel de
    // eroare, fiindca nu arata a eroare.
    this.eroarePas1 = document.createElement('p');
    this.eroarePas1.className = 'cod__eroare cod__eroare--general text-sm';
    this.eroarePas1.setAttribute('role', 'alert');
    this.eroarePas1.hidden = true;

    const continua = document.createElement('button');
    continua.type = 'button';
    continua.className = 'button cod__continua';
    continua.textContent = this.vorba('continua');
    continua.addEventListener('click', () => this.treciLaDate());
    this.continuaNod = continua;
    pasGravura.append(this.eroarePas1, continua);

    // Cat scrie in gravura, butonul isi arata starea. `aria-disabled`, nu
    // `disabled`: apasabil ramane — asa afla DE CE nu se poate merge mai
    // departe, in loc sa se uite la un buton stins.
    corpGravura.addEventListener('input', () => this.actualizeazaContinua());
    corpGravura.addEventListener('change', () => this.actualizeazaContinua());

    const inapoi = document.createElement('button');
    inapoi.type = 'button';
    inapoi.className = 'button button--outline cod__inapoi';
    inapoi.textContent = this.vorba('inapoi');
    inapoi.addEventListener('click', () => this.aratePasul(1));

    // „Modifica" chiar pe rezumat: acolo se uita omul cand verifica textul, si
    // tot acolo se intreaba daca mai poate schimba ceva.
    const rezumat = this.querySelector('[data-rol="rezumat"]');
    if (rezumat) {
      const modifica = document.createElement('button');
      modifica.type = 'button';
      modifica.className = 'cod__modifica';
      modifica.textContent = this.vorba('modifica');
      modifica.addEventListener('click', () => this.aratePasul(1));
      rezumat.append(modifica);
    }

    const pasDate = document.createElement('div');
    pasDate.className = 'cod__pas';
    pasDate.dataset.pas = '2';
    const titluDate = document.createElement('p');
    titluDate.className = 'cod__pas-titlu';
    titluDate.textContent = this.vorba('pasDate');
    pasDate.append(titluDate, this.panou);
    this.panou.hidden = false;
    // Inaintea notei GDPR, nu dupa: nota e ultima soapta a formularului, iar
    // un buton aparut sub ea ar parea ca face parte din alt ecran.
    const nota = this.panou.querySelector('.cod__gdpr');
    if (nota) this.panou.insertBefore(inapoi, nota);
    else this.panou.append(inapoi);

    const antet = document.createElement('div');
    antet.className = 'cod__antet';
    antet.append(this.progres, this.indicator);

    dialog.append(antet, pasGravura, pasDate, this.succes);
    this.append(dialog);

    this.dialog = dialog;
    this.pasi = [pasGravura, pasDate];

    // Cate segmente, atatia pasi: bara descrie drumul, nu o cifra fixa.
    for (let i = 0; i < this.pasi.length; i += 1) {
      const segment = document.createElement('span');
      segment.className = 'cod__progres-segment';
      this.progres.append(segment);
      this.progresSegmente.push(segment);
    }

    this.aratePasul(1);

    dialog.addEventListener('close', () => {
      if (this.succes.hidden) return;
      this.append(this.succes);
      this.deschide.hidden = true;
      this.querySelector('.cod__soapta')?.setAttribute('hidden', '');
    });
  }

  /**
   * Customizerul, dupa ce si-a randat campurile.
   *
   * `data-gata` acopera cazul in care randarea s-a terminat inaintea noastra —
   * altfel am astepta un eveniment care a trecut deja. Asteptarea are capat:
   * daca formularul de gravura nu porneste, comanda tot trebuie sa se poata
   * face.
   */
  asteaptaGravura() {
    const gravura = document.querySelector('gravura-form');
    if (!gravura) return Promise.resolve(null);
    if (gravura.dataset.gata === '1') return Promise.resolve(gravura);

    return new Promise((gata) => {
      const raspunde = () => gata(gravura);
      gravura.addEventListener('gravura:gata', raspunde, { once: true });
      setTimeout(raspunde, 8000);
    });
  }

  aratePasul(numar) {
    this.pasul = numar;
    for (const pas of this.pasi ?? []) pas.hidden = Number(pas.dataset.pas) !== numar;
    if (this.indicator) {
      this.indicator.textContent = this.vorba('pasul', { n: numar, total: this.pasi.length });
    }
    (this.progresSegmente ?? []).forEach((segment, index) => {
      segment.classList.toggle('cod__progres-segment--facut', index < numar);
    });
    this.dialog?.scrollTo?.({ top: 0 });

    // Focusul merge unde merge si privirea; altfel, la tastatura, ramane pe un
    // buton care tocmai a disparut.
    const pas = this.pasi?.[numar - 1];
    pas?.querySelector('input:not([type="hidden"]), textarea, select')?.focus({ preventScroll: true });
  }

  /**
   * Din gravura in datele de livrare.
   *
   * Trecerea o pazeste chiar customizerul: `motivulBlocarii()` spune de ce nu
   * se poate merge mai departe (text gol, font fara literele cerute), iar
   * `valideaza()` scrie proprietatile in formularul de produs — exact ce
   * citeste pasul urmator. Cine a bifat „trimit textul mai tarziu" trece:
   * pentru el nu e nimic de validat.
   */
  async treciLaDate() {
    const motiv = this.gravura?.motivulBlocarii?.();
    if (motiv) {
      this.spuneLaPasul1(motiv || this.vorba('gravuraDeCompletat'));
      return;
    }

    if (this.gravura?.stare?.maiTarziu) this.gravura.amanaTextul?.();
    else if (await this.gravura?.valideaza?.() === false) {
      // Serverul a refuzat ceva ce verificarea din browser n-a prins (un font
      // fara literele cerute, un text prea lung pentru zona). Mesajele apar in
      // formularul de gravura; aici doar nu mergem mai departe.
      this.spuneLaPasul1(this.vorba('gravuraDeCompletat'));
      return;
    }

    this.spuneLaPasul1(null);
    this.actualizeazaRezumatul();
    this.aratePasul(2);
  }

  /** Mesajul pasului 1 — sau stingerea lui. */
  spuneLaPasul1(mesaj) {
    if (!this.eroarePas1) return;
    if (!mesaj) { this.eroarePas1.hidden = true; return; }
    this.eroarePas1.textContent = mesaj;
    this.eroarePas1.hidden = false;
    this.eroarePas1.scrollIntoView({ block: 'nearest' });
  }

  /** Curier sau ridicare: arata si ascunde ce tine de fiecare drum. */
  alegeLivrarea(mod) {
    this.modLivrare = mod === 'magazin' ? 'magazin' : 'curier';
    for (const chip of this.querySelectorAll('[data-rol="mod-livrare"] [data-mod]')) {
      const activ = chip.dataset.mod === this.modLivrare;
      chip.classList.toggle('cod__chip--activ', activ);
      chip.setAttribute('aria-pressed', String(activ));
    }
    const panou = this.querySelector('[data-rol="ridicare"]');
    if (panou) panou.hidden = this.modLivrare !== 'magazin';
    for (const nume of CAMPURI_DOAR_CURIER) {
      const camp = this.querySelector(`[data-camp="${nume}"]`);
      if (camp) camp.hidden = this.modLivrare === 'magazin';
    }
  }

  /** Azi, maine sau o zi aleasa — calendarul apare doar la „alta zi". */
  alegeMomentul(cand) {
    this.ridicare = { cand, data: this.ridicare?.data };
    for (const chip of this.querySelectorAll('[data-rol="ridicare"] [data-cand]')) {
      const activ = chip.dataset.cand === cand;
      chip.classList.toggle('cod__chip--activ', activ);
      chip.setAttribute('aria-pressed', String(activ));
    }
    const ziua = this.querySelector('[data-rol="ridicare-data"]');
    if (ziua) {
      ziua.hidden = cand !== 'data';
      if (cand === 'data' && !ziua.value) ziua.focus();
    }
  }

  /** Ce pleaca la server despre livrare. */
  livrareaAleasa() {
    if (this.modLivrare !== 'magazin') return { mod: 'curier' };
    return {
      mod: 'magazin',
      cand: this.ridicare?.cand ?? 'maine',
      data: this.ridicare?.cand === 'data' ? (this.ridicare?.data ?? '') : undefined,
    };
  }

  /** Starea butonului „Continua", dupa ce spune customizerul. */
  actualizeazaContinua() {
    if (!this.continuaNod) return;
    const blocat = Boolean(this.gravura?.motivulBlocarii?.());
    this.continuaNod.setAttribute('aria-disabled', String(blocat));
    if (!blocat) this.spuneLaPasul1(null);
  }

  /**
   * „Achită acum" — plata cu cardul, fara ocolisuri.
   *
   * Nu toti clientii vor ramburs: unii au cardul in mana si vor sa termine.
   * Butonul pune piesa in cos cu tot cu gravura (aceleasi proprietati) si duce
   * DIRECT la checkout — fara pagina de cos, care aici n-ar spune nimic nou.
   *
   * Pe pagina de cos nu se adauga nimic: cosul e deja facut, se merge la plata.
   */
  async achitaAcum(nasture) {
    if (this.inZbor) return;
    const etichetaVeche = nasture.textContent;
    this.inZbor = true;
    nasture.disabled = true;
    nasture.textContent = this.vorbe.seTrimite;

    // Aceeasi poarta ca la ramburs: cosul se umple din formularul de produs,
    // deci o gravura nevalidata ar ajunge la plata fara nicio proprietate.
    const gata = await this.pregatesteGravura();
    if (!gata.ok) {
      this.inZbor = false;
      nasture.disabled = false;
      nasture.textContent = etichetaVeche;
      return this.opresteLaGravura(gata.motiv);
    }

    try {
      if (this.dataset.sursa !== 'cos') {
        const formular = document.getElementById(this.dataset.formular);
        const date = new FormData(formular);
        // `/cart/add.js` nu accepta campuri straine; se trimite doar ce stie.
        const raspuns = await fetch(`${radacinaMagazinului()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: date,
        });
        if (!raspuns.ok) throw new Error('adaugarea in cos a esuat');
      }
      window.location.href = `${radacinaMagazinului()}checkout`;
    } catch {
      const generala = this.querySelector('[data-rol="eroare-generala"]');
      if (generala) {
        generala.textContent = this.vorbe.esuata;
        generala.hidden = false;
      }
      this.inZbor = false;
      nasture.disabled = false;
      nasture.textContent = etichetaVeche;
    }
  }

  /** Banii, in valuta activa a vizitatorului. Intra subunitati, ies frumos. */
  baniFrumosi(subunitati) {
    const moneda = window.Shopify?.currency?.active ?? 'MDL';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ro', {
        style: 'currency', currency: moneda, maximumFractionDigits: 2,
      }).format((subunitati ?? 0) / 100);
    } catch { return `${((subunitati ?? 0) / 100).toFixed(2)} ${moneda}`; }
  }

  /**
   * Sugestiile produsului, legate in consola.
   *
   * Serverul da doar rolul, numele si poza; pretul si varianta vin din
   * `/products/<handle>.js` — valuta pietei, exact cifra de pe pagina piesei.
   * Tot de acolo vine si pretul numeric al piesei PRINCIPALE: cu o sugestie
   * bifata, butonul „Trimite" isi aduna totalul corect.
   */
  async incarcaUpsell() {
    try {
      const raspuns = await fetch(`/apps/gravura/upsell?handle=${encodeURIComponent(this.dataset.handle)}`);
      if (!raspuns.ok) return;
      const { articole } = await raspuns.json();

      // Pretul numeric al piesei principale, pe varianta — pentru total.
      try {
        const principal = await (await fetch(`${radacinaMagazinului()}products/${this.dataset.handle}.js`)).json();
        this.pretNumericVarianta = new Map((principal.variants ?? []).map((v) => [String(v.id), v.price]));
      } catch { this.pretNumericVarianta = null; }

      if (!Array.isArray(articole) || articole.length === 0) return;

      const complete = (await Promise.all(articole.map(async (articol) => {
        try {
          const produs = await (await fetch(`${radacinaMagazinului()}products/${articol.handle}.js`)).json();
          const varianta = (produs.variants ?? []).find((v) => v.available) ?? null;
          if (!varianta) return null;
          return {
            ...articol,
            varianta: String(varianta.id),
            pret: varianta.price,
            poza: articol.poza ?? produs.featured_image ?? null,
            adresa: produs.url ?? `/products/${articol.handle}`,
          };
        } catch { return null; }
      }))).filter(Boolean);
      if (complete.length === 0) return;

      this.upsell = complete;
      this.arataUpsellFormular(complete.filter((articol) => articol.rol === 'formular'));
      this.arataUpsellPagina(complete.filter((articol) => articol.rol === 'pagina'));
    } catch { /* sugestiile nu au voie sa strice comanda */ }
  }

  /** Cardurile bifabile din formular: „adauga si…" — intra in aceeasi comanda. */
  arataUpsellFormular(lista) {
    if (lista.length === 0 || !this.panou) return;
    const gazda = document.createElement('div');
    gazda.className = 'cod__impreuna';

    const titlu = document.createElement('p');
    titlu.className = 'cod__impreuna-titlu';
    titlu.textContent = this.vorba('impreuna');
    gazda.append(titlu);

    for (const articol of lista) {
      const card = document.createElement('label');
      card.className = 'cod__impreuna-card';

      const bifa = document.createElement('input');
      bifa.type = 'checkbox';
      bifa.className = 'cod__impreuna-bifa';
      bifa.addEventListener('change', () => {
        if (bifa.checked) this.upsellAlese.add(articol.varianta);
        else this.upsellAlese.delete(articol.varianta);
        card.classList.toggle('cod__impreuna-card--aleasa', bifa.checked);
        this.actualizeazaRezumatul();
      });

      const poza = document.createElement('img');
      poza.className = 'cod__impreuna-poza';
      poza.alt = '';
      poza.loading = 'lazy';
      poza.width = 88;
      poza.height = 88;
      if (articol.poza) poza.src = articol.poza;

      const text = document.createElement('span');
      text.className = 'cod__impreuna-text';
      const nume = document.createElement('span');
      nume.className = 'cod__impreuna-nume';
      nume.textContent = articol.titlu || articol.nume;
      const pret = document.createElement('span');
      pret.className = 'cod__impreuna-pret';
      pret.textContent = `+ ${this.baniFrumosi(articol.pret)}`;
      text.append(nume, pret);

      card.append(bifa, poza, text);
      gazda.append(card);
    }

    const reper = this.panou.querySelector('[data-rol="mod-livrare"]') ?? this.panou.firstChild;
    this.panou.insertBefore(gazda, reper);
  }

  /** Cardurile-legatura de sub zona de comanda: „poate-ti mai place". */
  arataUpsellPagina(lista) {
    if (lista.length === 0) return;
    const gazda = document.createElement('div');
    gazda.className = 'cod__descopera';

    const titlu = document.createElement('p');
    titlu.className = 'cod__impreuna-titlu';
    titlu.textContent = this.vorba('descopera');
    gazda.append(titlu);

    const randuri = document.createElement('div');
    randuri.className = 'cod__descopera-randuri';
    for (const articol of lista) {
      const card = document.createElement('a');
      card.className = 'cod__descopera-card';
      card.href = articol.adresa;

      const poza = document.createElement('img');
      poza.className = 'cod__descopera-poza';
      poza.alt = '';
      poza.loading = 'lazy';
      poza.width = 120;
      poza.height = 120;
      if (articol.poza) poza.src = articol.poza;

      const nume = document.createElement('span');
      nume.className = 'cod__descopera-nume';
      nume.textContent = articol.titlu || articol.nume;

      const pret = document.createElement('span');
      pret.className = 'cod__descopera-pret';
      pret.textContent = this.baniFrumosi(articol.pret);

      card.append(poza, nume, pret);
      randuri.append(card);
    }
    gazda.append(randuri);
    this.append(gazda);
  }

  comuta() {
    if (this.dialog) {
      this.arataAvertismentulGravurii();
      this.actualizeazaRezumatul();
      this.improspateazaDupaValidare();
      this.dialog.showModal();
      const pas = this.pasi ? this.pasi[this.pasul - 1] : this.panou;
      pas?.querySelector('input, textarea')?.focus();
      return;
    }
    const inchis = this.panou.hidden;
    this.panou.hidden = !inchis;
    this.deschide.setAttribute('aria-expanded', String(inchis));
    if (inchis) {
      this.arataAvertismentulGravurii();
      this.actualizeazaRezumatul();
      this.improspateazaDupaValidare();
      this.panou.querySelector('input')?.focus();
    }
  }

  /**
   * Macheta vine tarziu, iar rezumatul nu stia sa astepte.
   *
   * Rezumatul se deseneaza din proprietatile formularului de produs, dar pe
   * ele le scrie validarea TACUTA a gravurii — cu amanare si un drum la
   * server. Cine scria textul si apasa imediat „Comanda acum" prindea
   * rezumatul fara macheta si fara avertismente corecte, si asa ramaneau
   * (textele se vad oricum: se citesc direct din campuri). In quiz nu se
   * intampla — pasul 2 asteapta explicit validarea; in modurile popup/panou
   * n-o astepta nimeni. Aici: daca validarea n-a aterizat inca, o pornim,
   * o asteptam si redesenam rezumatul o singura data.
   */
  async improspateazaDupaValidare() {
    if (this.dataset.sursa === 'cos') return;
    const gravura = document.querySelector('gravura-form');
    if (typeof gravura?.valideaza !== 'function') return;
    if (gravura.motivulBlocarii?.() || gravura.stare?.maiTarziu) return;

    if (!this.gravuraLaZi(gravura)) {
      this.arataStareaMachetei('asteptare');
      try {
        await this.asteaptaValidarea(gravura);
      } catch { /* rezumatul ramane cum e; poarta de la trimitere prinde restul */ }
    }
    this.arataAvertismentulGravurii();
    this.actualizeazaRezumatul();
  }

  /** Jetonul de gravura e al textului de ACUM, nu al unuia de acum trei taste? */
  gravuraLaZi(gravura) {
    return Boolean(gravura?.stare?.jeton) && gravura.stare.amprenta === gravura.amprenta?.();
  }

  /**
   * Asteapta validarea gravurii, si o porneste daca n-a pornit nimeni.
   *
   * Validarea tacuta se declanseaza la 900 ms dupa ultima tasta. Cine scrie si
   * apasa imediat „Comanda acum" — sau cine se intoarce pe pagina cu textul
   * refacut din memoria browserului, unde NIMENI n-o porneste — ajunge aici
   * inainte ca ea sa fi rulat.
   */
  async asteaptaValidarea(gravura) {
    for (let pas = 0; pas < 20 && gravura.valideazaInMers; pas += 1) {
      await new Promise((maiIncolo) => setTimeout(maiIncolo, 150));
    }
    if (this.gravuraLaZi(gravura)) return true;

    const bun = await (gravura.valideazaLinistit?.() ?? gravura.valideaza({ tacut: true }));
    for (let pas = 0; pas < 20 && gravura.valideazaInMers; pas += 1) {
      await new Promise((maiIncolo) => setTimeout(maiIncolo, 150));
    }
    return bun !== false && this.gravuraLaZi(gravura);
  }

  /**
   * Poarta gravurii, inainte de ORICE trimitere.
   *
   * Comanda pleaca cu proprietatile scrise in formularul de produs, iar pe alea
   * le scrie validarea gravurii. Fara poarta asta, doua drumuri normale duceau
   * la o comanda fara NICIO urma de gravura — text, font, machetă, jeton, tot:
   *
   *   1. textul nu incape (crucea spune „19 caractere, sablonul permite 14"),
   *      formularul o scrie rosu, dar „Trimite comanda" ramanea activ;
   *   2. clientul se intoarce pe pagina cu textul refacut din memorie si apasa
   *      inainte ca validarea sa fi apucat sa ruleze.
   *
   * S-a intamplat pe comenzi platite. De aceea aici se ASTEAPTA validarea, si
   * un refuz opreste trimiterea in loc s-o lase sa treaca pe tacute.
   */
  async pregatesteGravura() {
    const gravura = document.querySelector('gravura-form');
    if (this.dataset.sursa === 'cos' || typeof gravura?.valideaza !== 'function') return { ok: true };
    // Cine a bifat „trimit textul mai tarziu" n-are ce valida: comanda merge
    // dinadins fara gravura, iar formularul a scris deja proprietatea ei.
    if (gravura.stare?.maiTarziu) return { ok: true };

    const motiv = gravura.motivulBlocarii?.();
    if (motiv) return { ok: false, motiv: gravura.motivulDetaliat?.() ?? motiv };

    let bun = false;
    try {
      bun = await this.asteaptaValidarea(gravura);
    } catch {
      return { ok: false, motiv: this.vorbe.esuata };
    }
    if (bun) return { ok: true };
    return { ok: false, motiv: gravura.motivulDetaliat?.() || this.vorbe.gravuraDeCorectat };
  }

  /**
   * Comanda s-a oprit din cauza gravurii — si se vede unde e de lucru.
   *
   * Mesajul singur n-ar ajunge: in modul cu fereastra, campul de corectat sta
   * ASCUNS in spatele ei. De aceea langa mesaj vine un buton care inchide
   * fereastra si duce la campul cu pricina, unde formularul isi scrie deja
   * motivul („19 caractere, sablonul permite 14").
   */
  opresteLaGravura(motiv) {
    const generala = this.querySelector('[data-rol="eroare-generala"]');
    if (!generala) return undefined;

    generala.replaceChildren();
    const text = document.createElement('span');
    text.textContent = motiv || this.vorbe.gravuraDeCorectat;
    generala.append(text);

    const gravura = document.querySelector('gravura-form');
    if (gravura) {
      const nasture = document.createElement('button');
      nasture.type = 'button';
      nasture.className = 'cod__leg';
      nasture.textContent = this.vorba('mergiLaGravura');
      nasture.addEventListener('click', () => {
        this.dialog?.close();
        if (!this.dialog) { this.panou.hidden = true; this.deschide.setAttribute('aria-expanded', 'false'); }
        gravura.scrollIntoView({ behavior: 'smooth', block: 'center' });
        gravura.querySelector('input[type="text"], textarea')?.focus({ preventScroll: true });
      });
      generala.append(nasture);
    }

    generala.hidden = false;
    generala.scrollIntoView({ block: 'nearest' });
    this.arataStareaMachetei('blocat');
    return undefined;
  }

  /**
   * De ce nu e (inca) o macheta: se lucreaza la ea, ori e ceva de corectat.
   *
   * Piesele care nu se graveaza, si cine a amanat textul, nu primesc nici loc
   * gol: acolo n-are ce sa apara, iar o dunga care asteapta la nesfarsit ar
   * parea o defectiune.
   */
  stareaMachetei(gravura) {
    if (this.dataset.sursa === 'cos' || !gravura || gravura.stare?.maiTarziu) return 'fara';
    const areText = Object.values(gravura.stare?.texte ?? {}).some((text) => String(text ?? '').trim());
    if (!areText) return 'fara';
    return gravura.motivulBlocarii?.() ? 'blocat' : 'asteptare';
  }

  /**
   * Ce se vede in locul machetei cat timp nu e macheta.
   *
   * Un loc gol care spune ce se intampla e altceva decat nimic: „nimic" arata
   * exact ca o gravura care nu s-a inregistrat.
   */
  arataStareaMachetei(stare, mesaj = null) {
    const gazda = this.querySelector('[data-rol="rezumat-asteapta"]');
    const macheta = this.querySelector('[data-rol="rezumat-macheta"]');
    if (!gazda) return;

    const nota = gazda.querySelector('[data-rol="rezumat-asteapta-nota"]');
    if (stare === 'asteptare' || stare === 'blocat') {
      gazda.hidden = false;
      gazda.classList.toggle('cod__rezumat-asteapta--blocat', stare === 'blocat');
      if (nota) {
        nota.textContent = mesaj
          ?? (stare === 'blocat' ? this.vorba('machetaDeCorectat') : this.vorba('machetaSePregateste'));
      }
      if (macheta) macheta.hidden = true;
      return;
    }
    gazda.hidden = true;
  }

  /**
   * „Ce comanzi", chiar deasupra campurilor.
   *
   * Un ramburs se confirma la telefon a doua zi. Pana atunci, singura dovada a
   * clientului e ce a vazut aici — de aceea rezumatul arata piesa, macheta
   * gravurii, varianta, cantitatea, pretul si TEXTELE gravate, nu doar un
   * total. Datele vin din formularul de produs, nu din memorie: daca omul a
   * schimbat varianta intre timp, aici se vede varianta noua.
   */
  actualizeazaRezumatul() {
    const rezumat = this.querySelector('[data-rol="rezumat"]');
    if (!rezumat) return;

    // In cos, rezumatul il scrie cosul insusi (mai multe piese, alte poze).
    //
    // Verificarea sta INAINTEA formularului de produs: pe pagina de cos nu
    // exista niciun `<form>` de produs, `getElementById('')` da null, iar
    // functia iesea inainte sa ajunga aici — rezumatul ramanea gol.
    if (this.dataset.sursa === 'cos') { this.rezumatulCosului(rezumat); return; }

    const formular = document.getElementById(this.dataset.formular);
    const date = formular ? new FormData(formular) : null;
    if (!date) return;

    const varianta = this.variante[String(date.get('id') ?? '')] ?? null;
    const cantitate = Number.parseInt(String(date.get('quantity') ?? '1'), 10) || 1;

    const poza = rezumat.querySelector('[data-rol="rezumat-poza"]');
    if (poza && varianta?.poza) poza.src = varianta.poza;

    const numeVarianta = rezumat.querySelector('[data-rol="rezumat-varianta"]');
    if (numeVarianta) {
      // „Default Title" e numele pe care Shopify il da unei piese fara optiuni:
      // pentru client nu inseamna nimic si nu se arata.
      const titlu = varianta?.titlu && varianta.titlu !== 'Default Title' ? varianta.titlu : '';
      const bucati = [titlu, cantitate > 1 ? this.vorba('rezumatCantitate', { n: cantitate }) : ''];
      numeVarianta.textContent = bucati.filter(Boolean).join(' · ');
      numeVarianta.hidden = !numeVarianta.textContent;
    }

    // --- Cat plateste, defalcat ---------------------------------------------
    //
    // Pretul piesei singur mintea: cine alegea o cutie de 300 vedea tot 800 si
    // in modal, si pe buton. Aici se aduna TOT ce pleaca in comanda — piesa,
    // cutia cadou, sugestiile bifate — si se arata din ce e facut.
    //
    // Toate cifrele se aduna in SUBUNITATI, ca `baniFrumosi`. Pretul cutiei vine
    // insa din configuratia gravurii ca zecimal (`contextualPricing`, 250.00),
    // nu in bani — de aceea se inmulteste cu 100. Fara conversia asta, 250 de lei
    // ar aduna 2,50.
    const pretPrincipal = this.pretNumericVarianta?.get(String(date.get('id') ?? ''));
    const cutie = document.querySelector('gravura-form')?.impachetareaAleasa?.() ?? null;
    const alese = [...(this.upsellAlese ?? [])];

    const linii = [];
    if (Number.isFinite(pretPrincipal)) {
      linii.push({ nume: this.vorba('rezumatPiesa'), bani: pretPrincipal * cantitate });
    }
    if (cutie && cutie.pret > 0) {
      linii.push({ nume: cutie.eticheta, bani: Math.round(cutie.pret * 100) });
    }
    // Mesajul de pe cutie, cand se factureaza separat. Ca text (mod
    // „proprietate") nu costa nimic si nu apare aici.
    if (cutie?.detaliu?.mod === 'linie' && cutie.detaliu.pret > 0) {
      linii.push({ nume: cutie.detaliu.eticheta, bani: Math.round(cutie.detaliu.pret * 100) });
    }
    for (const id of alese) {
      const articol = this.upsell?.find((a) => a.varianta === id);
      if (articol) linii.push({ nume: articol.nume, bani: articol.pret ?? 0 });
    }

    const potAduna = Number.isFinite(pretPrincipal);
    const total = linii.reduce((suma, l) => suma + l.bani, 0);

    const pret = rezumat.querySelector('[data-rol="rezumat-pret"]');
    if (pret) pret.textContent = potAduna ? this.baniFrumosi(total) : (varianta?.pret ?? '');

    // Defalcarea apare doar cand chiar e ceva de defalcat: la o piesa singura,
    // „Bijuterie 800 / Total 800" nu spune nimic si doar ocupa ecranul.
    const defalcare = rezumat.querySelector('[data-rol="rezumat-defalcare"]');
    if (defalcare) {
      defalcare.replaceChildren();
      const merita = potAduna && linii.length > 1;
      defalcare.hidden = !merita;
      if (merita) {
        for (const l of linii) {
          const rand = document.createElement('div');
          rand.className = 'cod__rezumat-rand';
          const nume = document.createElement('span');
          nume.textContent = l.nume;
          const bani = document.createElement('span');
          bani.textContent = this.baniFrumosi(l.bani);
          rand.append(nume, bani);
          defalcare.append(rand);
        }
      }
    }

    // Cifra si pe buton: ultimul lucru pe care il vede inainte sa apese.
    if (this.trimiteEticheta) {
      const scris = potAduna ? this.baniFrumosi(total) : (varianta?.pret ?? '');
      this.trimite.textContent = scris ? `${this.trimiteEticheta} · ${scris}` : this.trimiteEticheta;
    }

    // Proprietatile de gravura, cum le vede si comanda. Cele ascunse (jetonul)
    // si macheta au locul lor — jetonul nicaieri, macheta ca imagine.
    const lista = rezumat.querySelector('[data-rol="rezumat-gravura"]');
    const macheta = rezumat.querySelector('[data-rol="rezumat-macheta"]');
    if (!lista) return;

    lista.replaceChildren();
    let adresaMachetei = null;

    const randStrict = (eticheta, valoare) => {
      const nume = document.createElement('dt');
      nume.textContent = eticheta;
      const nod = document.createElement('dd');
      nod.textContent = valoare;
      lista.append(nume, nod);
    };

    // Macheta e imagine, nu rand de text — se culege intotdeauna.
    for (const [cheie, valoare] of date.entries()) {
      const potrivire = cheie.match(/^properties\[(.+)\]$/);
      const text = String(valoare).trim();
      if (potrivire && text && /machet/i.test(potrivire[1])) adresaMachetei = text;
    }

    /*
     * Etichetele sunt ALE FORMULARULUI, nu numele proprietatilor de comanda.
     *
     * Pe o bratara impletita, campul se numeste „Gravură pe spate (interior)"
     * — asa l-a citit clientul cand a scris. Proprietatea de comanda se
     * numeste insa „Gravură spate", fiindca acolo conteaza sa fie un nume
     * stabil pentru atelier. Rezumatul e pentru CLIENT: el trebuie sa
     * recunoasca exact randurile pe care tocmai le-a completat.
     */
    const gravura = document.querySelector('gravura-form');
    const dinFormular = gravura?.rezumatulCampurilor?.() ?? [];

    if (dinFormular.length > 0) {
      for (const rand of dinFormular) {
        randStrict(rand.eticheta, rand.poza ? this.vorba('rezumatPoza') : rand.valoare);
      }
    } else {
      // Fara formular de gravura pe pagina (piesa nu se graveaza, ori s-a
      // incarcat dintr-un link vechi): raman numele proprietatilor.
      for (const [cheie, valoare] of date.entries()) {
        const potrivire = cheie.match(/^properties\[(.+)\]$/);
        const text = String(valoare).trim();
        if (!potrivire || !text) continue;
        const nume = potrivire[1];
        if (nume.startsWith('_') || /machet/i.test(nume)) continue;
        randStrict(nume, text);
      }
    }

    if (macheta) {
      if (adresaMachetei) {
        // O machetă care nu se încarcă nu lasă o iconiță ruptă în rezumat: se
        // stinge, iar locul ei spune de ce.
        macheta.onerror = () => { macheta.hidden = true; this.arataStareaMachetei('asteptare'); };
        if (macheta.getAttribute('src') !== adresaMachetei) macheta.src = adresaMachetei;
        macheta.hidden = false;
        this.arataStareaMachetei('gata');
      } else {
        macheta.hidden = true;
        this.arataStareaMachetei(this.stareaMachetei(gravura));
      }
    }

    if (!lista.children.length) {
      const gol = document.createElement('dd');
      gol.className = 'cod__rezumat-gol';
      gol.textContent = this.vorba('rezumatFaraGravura');
      lista.append(gol);
    }
  }

  /**
   * Rezumatul cand comanda vine din cos: cate un rand per piesa.
   *
   * Nu se inventeaza nimic — se citeste chiar cosul, cu pozele, cantitatile si
   * preturile pe care le-a vazut deja clientul pe pagina de cos.
   */
  async rezumatulCosului(rezumat) {
    let cos;
    try {
      cos = await (await fetch(`${radacinaMagazinului()}cart.js`, {
        headers: { Accept: 'application/json' },
      })).json();
    } catch { return; }

    const bucati = cos.items ?? [];
    const bani = (subunitati) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'ro', {
          style: 'currency', currency: cos.currency, maximumFractionDigits: 2,
        }).format((subunitati ?? 0) / 100);
      } catch { return `${((subunitati ?? 0) / 100).toFixed(2)} ${cos.currency}`; }
    };

    // Antetul de produs (poza mare + machetă) nu are ce cauta aici: pe patru
    // piese ar arata UNA singura si ar spune ca aia se comanda. Se ascunde, iar
    // fiecare piesa isi primeste randul ei, cu poza ei.
    rezumat.querySelector('.cod__rezumat-poze')?.setAttribute('hidden', '');
    rezumat.querySelector('[data-rol="rezumat-varianta"]')?.setAttribute('hidden', '');
    rezumat.querySelector('[data-rol="rezumat-gravura"]')?.setAttribute('hidden', '');
    rezumat.classList.add('cod__rezumat--cos');

    const titlu = rezumat.querySelector('.cod__rezumat-titlu');
    if (titlu) titlu.textContent = this.vorba('rezumatCos', { n: bucati.length });
    const pret = rezumat.querySelector('[data-rol="rezumat-pret"]');
    if (pret) pret.textContent = bani(cos.total_price);

    // Lista de piese, construita de la zero la fiecare deschidere: cosul se
    // poate schimba intre doua apasari, iar un rand ramas din urma ar fi o
    // comanda pe care omul crede ca a facut-o.
    const veche = rezumat.querySelector('.cod__cos');
    if (veche) veche.remove();

    const lista = document.createElement('ul');
    lista.className = 'cod__cos';

    for (const linie of bucati) {
      const rand = document.createElement('li');
      rand.className = 'cod__cos-linie';

      const poza = document.createElement('img');
      poza.className = 'cod__cos-poza';
      poza.alt = '';
      poza.loading = 'lazy';
      poza.width = 112;
      poza.height = 112;
      if (linie.image) poza.src = linie.image;

      const text = document.createElement('div');
      text.className = 'cod__cos-text';

      const nume = document.createElement('p');
      nume.className = 'cod__cos-titlu';
      nume.textContent = linie.product_title;
      text.append(nume);

      // Varianta („Gold", „45 cm") — parte din ce comanda, nu detaliu de stil.
      if (linie.variant_title && linie.variant_title !== 'Default Title') {
        const varianta = document.createElement('p');
        varianta.className = 'cod__cos-varianta';
        varianta.textContent = linie.variant_title;
        text.append(varianta);
      }

      // Personalizarea, DESFACUTA pe etichete. Concatenata („Un pas o dată ♥ ·
      // Tu și eu ♥ · Script elegant · Premium Neagra · Da") nu se poate citi:
      // nu se stie ce e text gravat, ce e font si ce e cutie.
      // Etichetele foarte lungi se scurteaza la ce inseamna: pe un rand de
      // rezumat, „Previzualizare inainte de livrare" ocupa mai mult decat
      // raspunsul „Da" pe care il eticheteaza.
      const detalii = Object.entries(linie.properties ?? {})
        .filter(([cheie, valoare]) => !cheie.startsWith('_')
          && !/machet/i.test(cheie)
          && String(valoare ?? '').trim() !== '')
        .map(([cheie, valoare]) => [ETICHETE_SCURTE[cheie] ?? cheie, valoare]);

      if (detalii.length > 0) {
        const dl = document.createElement('dl');
        dl.className = 'cod__cos-detalii';
        for (const [cheie, valoare] of detalii) {
          const dt = document.createElement('dt');
          dt.textContent = cheie;
          const dd = document.createElement('dd');
          dd.textContent = String(valoare);
          dl.append(dt, dd);
        }
        text.append(dl);
      }

      const bani2 = document.createElement('div');
      bani2.className = 'cod__cos-bani';
      const pretLinie = document.createElement('p');
      pretLinie.className = 'cod__cos-pret';
      pretLinie.textContent = bani(linie.line_price ?? linie.final_line_price);
      bani2.append(pretLinie);

      // Cantitatea se scrie DOAR cand e mai mare de una: un „× 1" pe fiecare
      // rand e zgomot care ascunde exact randul unde chiar sunt doua.
      if ((linie.quantity ?? 1) > 1) {
        const cant = document.createElement('p');
        cant.className = 'cod__cos-cant';
        cant.textContent = `× ${linie.quantity}`;
        bani2.append(cant);
      }

      rand.append(poza, text, bani2);
      lista.append(rand);
    }

    rezumat.append(lista);
  }

  /**
   * Piesa are formular de gravura, dar clientul n-a scris inca textul: comanda
   * merge oricum (rambursul se confirma telefonic), doar ca i-o spunem cinstit
   * INAINTE, nu-l lasam sa creada ca gravura s-a cerut deja.
   */
  arataAvertismentulGravurii() {
    const nota = this.querySelector('[data-rol="gravura-lipsa"]');
    if (!nota) return;
    // In cos, gravura s-a completat (sau nu) la fiecare piesa in parte —
    // un avertisment global ar vorbi despre o piesa anume, si nu se stie care.
    if (this.dataset.sursa === 'cos') { nota.hidden = true; return; }
    const formular = document.getElementById(this.dataset.formular);
    const areGravura = Boolean(document.querySelector('gravura-form'));
    // Prin FormData, nu querySelector: controalele scrise de formularul de
    // gravura pot sta IN AFARA <form>-ului, legate prin atributul `form` —
    // FormData le vede pe toate, interogarea pe subarbore nu.
    const date = formular ? new FormData(formular) : null;
    const areJeton = Boolean(date?.get('properties[_gravura]'))
      || Boolean(date?.get('properties[_gravura_mai_tarziu]'));
    // Nota spune „textul nu e completat — te sunam noi". Cine A SCRIS ceva nu e
    // in cazul asta: ori se valideaza chiar acum, ori e ceva de corectat, si
    // pentru amandoua raspunde macheta din rezumat, nu o promisiune de telefon.
    const stare = this.stareaMachetei(document.querySelector('gravura-form'));
    nota.hidden = !areGravura || areJeton || stare === 'asteptare' || stare === 'blocat';
  }

  valoare(camp) {
    return String(this.querySelector(`[data-camp="${camp}"] input, [data-camp="${camp}"] select`)?.value ?? '').trim();
  }

  arataEroarea(camp, mesaj) {
    // Nu tot ce refuza serverul are un camp pe ecran — ridicarea, de pilda, e
    // un rand de chips-uri, iar pe o comanda din Romania e chiar ascuns. Un
    // mesaj pus intr-un loc ascuns e un refuz fara niciun cuvant pe ecran, asa
    // ca se cauta primul loc care se si VEDE.
    const vazut = (nod) => nod && !nod.closest('[hidden]');
    const nod = [
      this.querySelector(`[data-camp="${camp}"] [data-rol="eroare"]`),
      this.querySelector(`[data-rol="eroare-${camp}"]`),
      this.querySelector('[data-rol="eroare-generala"]'),
    ].find(vazut) ?? this.querySelector('[data-rol="eroare-generala"]');
    if (!nod) return;
    nod.textContent = mesaj;
    nod.hidden = false;
  }

  /** Regulile, o singura data scrise: le folosesc si blur-ul, si trimiterea. */
  problemaCampului(nume) {
    if (nume === 'nume') return this.valoare('nume').length < 3 ? this.vorbe.numeScurt : null;
    if (nume === 'telefon') {
      return telefonBun(this.valoare('telefon'), this.valoare('tara')) ? null : this.vorbe.telefonInvalid;
    }
    // Optional: gol trece. Scris gresit NU trece — o adresa cu o litera in
    // plus inseamna o confirmare care pleaca in gol si un client care crede
    // ca a primit-o.
    if (nume === 'email') return emailBun(this.valoare('email')) ? null : this.vorbe.emailInvalid;
    if (nume === 'oras') return this.valoare('oras').length < 2 ? this.vorbe.orasLipsa : null;
    if (nume === 'adresa') return this.valoare('adresa').length < 5 ? this.vorbe.adresaScurta : null;
    return null;
  }

  /** La iesirea din camp: se striga doar ce a fost ATINS si e gresit. */
  verificaUnul(nume) {
    const control = this.querySelector(`[data-camp="${nume}"] input`);
    if (!control || !control.value.trim()) return;
    const problema = this.problemaCampului(nume);
    if (problema) this.arataEroarea(nume, problema);
  }

  /** Verificarea locala — oglinda celei de pe server, ca refuzul sa fie instant. */
  verifica() {
    const erori = [];
    const cerute = this.modLivrare === 'magazin'
      ? ['nume', 'telefon']
      : ['nume', 'telefon', 'oras', 'adresa'];
    // Emailul nu e cerut, dar dacă e scris se verifică: `problemaCampului`
    // trece gol-ul, deci lista poate fi una singură.
    for (const nume of [...cerute, 'email']) {
      const problema = this.problemaCampului(nume);
      if (problema) erori.push([nume, problema]);
    }

    for (const [camp, mesaj] of erori) this.arataEroarea(camp, mesaj);
    if (erori.length > 0) {
      this.querySelector(`[data-camp="${erori[0][0]}"] input`)?.focus();
      return false;
    }

    // „Altă zi" fara zi aleasa: serverul o refuza oricum, dar refuzul lui vine
    // dupa un drum la retea si fara sa arate CARE camp lipseste.
    const ziua = this.querySelector('[data-rol="ridicare-data"]');
    const eroareRidicare = this.querySelector('[data-rol="eroare-ridicare"]');
    if (this.modLivrare === 'magazin' && this.ridicare?.cand === 'data' && !ziua?.value) {
      if (eroareRidicare) {
        eroareRidicare.textContent = this.vorba('ridicareAlegeZiua');
        eroareRidicare.hidden = false;
      }
      ziua?.focus();
      return false;
    }
    if (eroareRidicare) eroareRidicare.hidden = true;

    return true;
  }

  /** Varianta, cantitatea si proprietatile — din formularul de produs, la click. */
  dinFormularulDeProdus() {
    const formular = document.getElementById(this.dataset.formular);
    if (!formular) return null;

    const date = new FormData(formular);
    const proprietati = [];
    for (const [cheie, valoare] of date.entries()) {
      const potrivire = cheie.match(/^properties\[(.+)\]$/);
      if (potrivire && String(valoare).trim() !== '') {
        proprietati.push({ key: potrivire[1], value: String(valoare) });
      }
    }

    return {
      varianta: String(date.get('id') ?? ''),
      cantitate: Number.parseInt(String(date.get('quantity') ?? '1'), 10) || 1,
      proprietati,
    };
  }

  /**
   * Ce se comanda: piesa de pe pagina, sau TOT cosul.
   *
   * Pe pagina de produs, un singur articol — cel din formular. In cos,
   * `/cart.js` da liniile asa cum le-a pus clientul, cu proprietatile lor de
   * gravura intacte: cine a personalizat doua piese si apasa „achita la
   * primire" trebuie sa le primeasca pe amandoua, nu pe ultima.
   */
  /** Sugestiile bifate, ca linii de comanda — fara proprietati de gravura. */
  articoleleUpsell() {
    return [...(this.upsellAlese ?? [])].map((varianta) => ({
      varianta, cantitate: 1, proprietati: [],
    }));
  }

  /**
   * Cutia cadou, ca linie de comanda.
   *
   * Pe drumul prin cos, cutia se adauga singura (`gravura.js`, cart/add.js).
   * Comanda ramburs insa NU trece prin cos: fara linia asta, clientul alegea
   * „Premium Negru +" de 300, atelierul o impacheta, si comanda pleca facturata
   * doar cu bijuteria. Cutia era o proprietate de text, nu un produs.
   *
   * Nu poarta proprietati de gravura: nu se graveaza o cutie.
   */
  articolulImpachetarii() {
    const gravura = document.querySelector('gravura-form');
    const cutie = gravura?.impachetareaAleasa?.();
    if (!cutie) return [];
    const linii = [{ varianta: String(cutie.numar), cantitate: 1, proprietati: [] }];
    // Mesajul de pe cutie, ca linie separata cand se factureaza. Proprietatea
    // spune de care cutie tine, ca atelierul sa nu ghiceasca.
    const detaliu = cutie.detaliu;
    if (detaliu?.mod === 'linie' && detaliu.numar) {
      linii.push({ varianta: String(detaliu.numar), cantitate: 1, proprietati: [{ key: 'Pentru cutia', value: cutie.eticheta }] });
    }
    return linii;
  }

  async articoleleComenzii() {
    if (this.dataset.sursa !== 'cos') {
      const produs = this.dinFormularulDeProdus();
      return produs ? [produs, ...this.articolulImpachetarii(), ...this.articoleleUpsell()] : [];
    }

    const raspuns = await fetch(`${radacinaMagazinului()}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const cos = await raspuns.json();

    return (cos.items ?? []).map((linie) => ({
      varianta: String(linie.variant_id ?? linie.id ?? ''),
      cantitate: Number(linie.quantity) || 1,
      proprietati: Object.entries(linie.properties ?? {})
        .filter(([, valoare]) => String(valoare ?? '').trim() !== '')
        .map(([key, value]) => ({ key, value: String(value) })),
    }));
  }

  async trimiteComanda() {
    if (this.inZbor || !this.verifica()) return;

    const generala = this.querySelector('[data-rol="eroare-generala"]');
    generala.hidden = true;

    // Gravura INTAI: proprietatile piesei se citesc din formularul de produs,
    // iar acolo le scrie validarea. Fara asta, o comanda putea pleca fara text,
    // fara font si fara machetă — si nimeni n-avea de unde sti.
    const etichetaVeche = this.trimite.textContent;
    this.trimite.textContent = this.vorbe.seTrimite;
    this.trimite.disabled = true;
    const gata = await this.pregatesteGravura();
    this.trimite.textContent = etichetaVeche;
    this.trimite.disabled = false;
    if (!gata.ok) return this.opresteLaGravura(gata.motiv);

    let articole = [];
    try {
      articole = await this.articoleleComenzii();
    } catch { articole = []; }

    if (articole.length === 0) {
      generala.textContent = this.vorbe.cosGol ?? this.vorbe.esuata;
      generala.hidden = false;
      return undefined;
    }

    this.inZbor = true;
    this.trimite.disabled = true;
    this.trimite.textContent = this.vorbe.seTrimite;

    try {
      const raspuns = await fetch('/apps/gravura/cod', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nume: this.valoare('nume'),
          telefon: this.valoare('telefon'),
          email: this.valoare('email'),
          tara: this.valoare('tara'),
          oras: this.valoare('oras'),
          adresa: this.valoare('adresa'),
          livrare: this.livrareaAleasa(),
          judet: this.valoare('judet'),
          postal: this.valoare('postal'),
          nota: this.valoare('nota'),
          website: this.valoare('website'),
          articole,
        }),
      });
      const corp = await raspuns.json().catch(() => ({}));

      if (raspuns.ok && corp.ok) {
        this.salveazaAdresa();
        return this.arataSuccesul(corp);
      }

      if (corp.eroare === 'date_invalide' && corp.campuri) {
        const MESAJE = {
          nume: this.vorbe.numeScurt, telefon: this.vorbe.telefonInvalid,
          email: this.vorbe.emailInvalid,
          oras: this.vorbe.orasLipsa, adresa: this.vorbe.adresaScurta,
          ridicare: corp.campuri.ridicare === 'ridicare_doar_md'
            ? this.vorba('ridicareDoarMd')
            : this.vorba('ridicareAlegeZiua'),
        };
        for (const camp of Object.keys(corp.campuri)) {
          this.arataEroarea(camp, MESAJE[camp] ?? this.vorbe.esuata);
        }
        return undefined;
      }

      const DUPA_COD = { comanda_recenta: this.vorbe.comandaRecenta, prea_multe_cereri: this.vorbe.preaMulte };
      generala.textContent = DUPA_COD[corp.eroare] ?? this.vorbe.esuata;
      generala.hidden = false;
      generala.scrollIntoView({ block: 'nearest' });
      return undefined;
    } catch {
      generala.textContent = this.vorbe.esuata;
      generala.hidden = false;
      return undefined;
    } finally {
      this.inZbor = false;
      this.trimite.disabled = false;
      this.trimite.textContent = this.trimiteEticheta;
    }
  }

  /**
   * Evenimentul de cumparare, din browser.
   *
   * Perechea lui pleaca de pe server (Conversions API) cu ACELASI `event_id`,
   * iar Facebook le contopeste. Fara id comun, o comanda s-ar numara de doua
   * ori si bugetele s-ar optimiza pe cifre false.
   *
   * Pixelul se incarca ABIA acum, la prima comanda: pe o pagina de produs
   * n-are ce urmari inca, iar un script de urmarire pornit degeaba e si
   * incetineala, si o alegere pe care n-am facut-o in numele clientului.
   */
  async raporteazaCumpararea(corp) {
    try {
      const raspuns = await fetch('/apps/gravura/marketing');
      const { pixelId } = await raspuns.json();
      if (!pixelId) return;

      if (!window.fbq) {
        /* eslint-disable */
        (function (f, b, e, v, n, t, s) {
          if (f.fbq) return; n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
          t = b.createElement(e); t.async = true; t.src = v;
          s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js'));
        /* eslint-enable */
        window.fbq('init', pixelId);
      }

      const articole = await this.articoleleComenzii().catch(() => []);
      window.fbq('track', 'Purchase', {
        value: corp.total?.amount ? Number(corp.total.amount) : undefined,
        currency: corp.total?.currencyCode,
        content_type: 'product',
        content_ids: articole.map((articol) => articol.varianta).filter(Boolean),
      }, corp.eventId ? { eventID: corp.eventId } : undefined);
    } catch { /* raportarea nu are voie sa strice confirmarea comenzii */ }
  }

  arataSuccesul(corp) {
    this.raporteazaCumpararea(corp);
    // In quiz, confirmarea inlocuieste pasii: nu se mai poate „inapoi" la un
    // formular care tocmai a devenit o comanda.
    for (const pas of this.pasi ?? []) pas.hidden = true;
    this.dialog?.querySelector('.cod__antet')?.setAttribute('hidden', '');
    this.panou.hidden = true;

    if (!this.dialog) {
      this.deschide.hidden = true;
      this.querySelector('.cod__soapta')?.setAttribute('hidden', '');
    }

    const numar = this.succes.querySelector('[data-rol="numar"]');
    if (numar) numar.textContent = corp.comanda ?? '';

    if (corp.total?.amount) {
      const rand = this.succes.querySelector('[data-rol="rand-total"]');
      const total = this.succes.querySelector('[data-rol="total"]');
      if (total) total.textContent = `${corp.total.amount} ${corp.total.currencyCode}`;
      rand?.removeAttribute('hidden');
    }

    // Numele lui, nu „stimate client": l-a scris cu doua randuri mai sus.
    const titlu = this.succes.querySelector('[data-rol="succes-titlu"]');
    const prenume = (this.valoare('nume') || '').trim().split(/\s+/)[0];
    if (titlu && prenume.length > 1) titlu.textContent = this.vorba('multumimNume', { nume: prenume });

    // Confirmarea pe email, spusa doar cand chiar pleaca una: fara adresa,
    // Shopify n-are unde s-o trimita, iar un rand care o promite ar fi minciuna.
    const randEmail = this.succes.querySelector('[data-rol="succes-email"]');
    const email = this.valoare('email');
    if (randEmail && email) {
      randEmail.textContent = this.vorba('confirmareEmail', { email });
      randEmail.hidden = false;
    }

    // Ultimul lucru pe care il vede: ce a trimis si ce va iesi.
    this.arataPozaComenzii();
    this.arataMachetaComenzii();

    if (this.modLivrare === 'magazin') {
      const pas = this.succes.querySelector('[data-rol="pas-final"]');
      // `innerHTML` cu textul NOSTRU, din dictionarul scris in tema (are un
      // <strong> in el). Nimic din ce scrie clientul nu trece pe aici.
      if (pas) pas.innerHTML = this.vorba('pasRidicare');
      const eticheta = this.succes.querySelector('[data-rol="eticheta-total"]');
      if (eticheta) eticheta.textContent = this.vorba('totalDeAchitat');
      this.arataRidicarea();
    }

    this.succes.hidden = false;
    this.succes.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Poza clientului, pe confirmare.
   *
   * Se ia MINIATURA LOCALA din formularul de gravura — un `blob:` facut din
   * fisierul lui, care nu iese niciodata din browser. Nu adresa semnata: o poza
   * de ochi e data biometrica si n-are de ce sa stea intr-un atribut din
   * pagina, aceeasi regula ca la incarcare. Cine n-a incarcat nimic, si cine
   * si-a incarcat poza in alta sesiune (starea o stie, blobul nu mai exista),
   * nu vede nimic aici — mai bine decat un patrat gol.
   */
  arataPozaComenzii() {
    const figura = this.succes?.querySelector('[data-rol="succes-poza"]');
    const poza = figura?.querySelector('[data-rol="succes-poza-poza"]');
    if (!figura || !poza || this.dataset.sursa === 'cos') return;

    const miniatura = document.querySelector('gravura-form [data-rol="miniatura"]');
    const adresa = miniatura?.getAttribute('src');
    if (!adresa) return;

    poza.onerror = () => { figura.hidden = true; };
    poza.src = adresa;
    figura.hidden = false;
  }

  /**
   * Macheta, pe confirmare.
   *
   * Se ia din formularul de produs — de unde pleaca si pe comanda — nu se mai
   * cere nimic serverului. Pe o piesa fara gravura, si pe una unde clientul a
   * amanat textul, nu apare nimic: acolo n-ar avea ce arata.
   */
  arataMachetaComenzii() {
    const figura = this.succes?.querySelector('[data-rol="succes-macheta"]');
    const poza = figura?.querySelector('[data-rol="succes-macheta-poza"]');
    if (!figura || !poza || this.dataset.sursa === 'cos') return;

    const formular = document.getElementById(this.dataset.formular);
    if (!formular) return;

    let adresa = null;
    for (const [cheie, valoare] of new FormData(formular).entries()) {
      const potrivire = cheie.match(/^properties\[(.+)\]$/);
      if (potrivire && /machet/i.test(potrivire[1]) && String(valoare).trim()) {
        adresa = String(valoare).trim();
      }
    }
    if (!adresa) return;

    poza.onerror = () => { figura.hidden = true; };
    poza.src = adresa;
    figura.hidden = false;
  }

  /**
   * Unde vine omul dupa piesa, si cum ajunge acolo.
   *
   * Se arata DUPA comanda, nu inainte: pana atunci adresa magazinului nu-i
   * spune nimic, iar dupa, e singurul lucru de care are nevoie. Harta se
   * construieste abia acum — un `<iframe>` catre Google in markup s-ar incarca
   * pe fiecare pagina de produs, si pentru fiecare vizitator care alege
   * curierul degeaba.
   */
  arataRidicarea() {
    const bloc = this.succes?.querySelector('[data-rol="succes-ridicare"]');
    if (!bloc) return;

    const cand = this.querySelector('[data-rol="succes-ridicare-cand"]');
    if (cand) {
      const ales = this.ridicare?.cand ?? 'maine';
      const scris = ales === 'data'
        ? this.vorba('ridicareData', { data: this.dataFrumoasa(this.ridicare?.data) })
        : this.vorba(ales === 'azi' ? 'ridicareAzi' : 'ridicareMaine');
      cand.textContent = this.vorba('ridicareCand', { cand: scris });
    }

    /*
     * Punctul: coordonatele fisei Mimilux de pe Google Maps.
     *
     * Cu ele, si pinul, si drumul cad pe magazin — cautarea dupa text ducea in
     * centrul comercial, adica la intrare, nu la etajul 2. Textul ramane ca
     * plasa: daca cineva sterge coordonatele din sablon, adresa scrisa tot
     * gaseste locul.
     */
    const lat = Number(this.dataset.ridicareLat);
    const lng = Number(this.dataset.ridicareLng);
    const punct = Number.isFinite(lat) && Number.isFinite(lng)
      ? `${lat},${lng}`
      : (this.dataset.ridicareHarta || this.dataset.ridicareAdresa || '');
    if (!punct) return;

    const directii = this.succes.querySelector('[data-rol="succes-directii"]');
    if (directii) {
      directii.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(punct)}`;
    }

    const gazda = this.succes.querySelector('[data-rol="succes-harta"]');
    if (gazda && !gazda.firstChild) {
      const harta = document.createElement('iframe');
      harta.className = 'cod__harta-cadru';
      harta.loading = 'lazy';
      harta.title = this.vorba('hartaTitlu');
      harta.referrerPolicy = 'no-referrer-when-downgrade';
      harta.allowFullscreen = false;
      harta.src = `https://www.google.com/maps?q=${encodeURIComponent(punct)}&hl=${this.dataset.limba === 'en' ? 'en' : 'ro'}&z=17&output=embed`;
      gazda.append(harta);
    }

    bloc.hidden = false;
  }

  /** Ziua aleasa, scrisa ca pentru un om („12 septembrie 2026"). */
  dataFrumoasa(iso) {
    if (!iso) return '';
    try {
      return new Intl.DateTimeFormat(this.dataset.limba === 'en' ? 'en-GB' : 'ro-RO', {
        day: 'numeric', month: 'long', year: 'numeric',
      }).format(new Date(`${iso}T12:00:00`));
    } catch { return iso; }
  }
}

if (!customElements.get('cod-form')) customElements.define('cod-form', CodForm);
